import { useEffect, useState } from 'react';

import { PATH_CONFIG } from '../../../../../../../config/static/path.config';
import { usePanoramaOnClick } from '../../../../../../../PanoramaLibrary/hooks/usePanoramaOnClick';
import { usePanoramaSynchronizeCameraPositions } from '../../../../../../../PanoramaLibrary/hooks/usePanoramaSynchronizeCameraPositions';
import { createVirtualTourConfigurationFromStore } from '../../../../../../../TourBuilderLibrary/domain/tourBuilderStore';
import { useTourBuilderStore } from '../../../../../../../TourBuilderLibrary/domain/tourBuilderStoreContext';
import { SceneLink, SceneLinkHelper } from '../../../../../../../TourBuilderLibrary/domain/types/SceneLink';
import { useOnMount } from '../../../../../../hooks/useOnMount';
import { hotspotRendererFactory } from '../../../../../../store/middleware/panorama/getHotspotRenderer';
import { IPanorama, PanoramaV2, PanoramaView } from '@ael/core-api';

export interface UseLocatingInPanoramaParams {
  panoramaId: string;
  targetId: string;
  onClick?: (panorama: IPanorama) => void;
  originalPanorama: IPanorama;
  onSceneChanged?: (panorama: IPanorama) => void;
  onReady?: (panorama: IPanorama) => void;
  view: PanoramaView;

  // Temp workaround for SceneLink positioning
  filterSceneLink?: (sceneLink: SceneLink) => boolean;
  isManager: boolean;
}

export const useLocatingInDraftPanorama = (params: UseLocatingInPanoramaParams) => {
  const [draftPanorama, setDraftPanorama] = useState<IPanorama | null>(null);
  const tourBuilderStore = useTourBuilderStore();

  useOnMount(() => {
    const virtualTourConfig = createVirtualTourConfigurationFromStore(tourBuilderStore.getState());
    const currentView = params.originalPanorama.getCameraPosition();
    PanoramaV2.Create(
      virtualTourConfig,
      params.panoramaId,
      PATH_CONFIG.SKIN_URL,
      hotspotRendererFactory(params.isManager),
      window.embedpano,
      currentView,
      params.targetId
    ).then((panorama: IPanorama) => {
      const filter = params.filterSceneLink ?? (() => true);
      tourBuilderStore.getState().links.forEach((link) => {
        if (filter(link)) {
          const sceneLinkHotspot = SceneLinkHelper.toSceneLinkHotspot(link);
          sceneLinkHotspot.attach(panorama);
        }
      });

      panorama.synchronizeCameraPositionTo(params.originalPanorama);
      setDraftPanorama(panorama);
      params.onReady?.(panorama);
    });
  });

  usePanoramaSynchronizeCameraPositions(params.originalPanorama, draftPanorama, params.view);

  usePanoramaOnClick(draftPanorama, params.onClick);

  // Clear the panorama on unmount
  useEffect(() => {
    return () => {
      draftPanorama?.dispose();
    };
  }, [draftPanorama]);

  return { draftPanorama };
};
